<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAdminSetupStore } from '@/store/AdminSetupStore';
import { usePromoComposable } from '@/components/admin/usePromoComposable';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import InputNumber from 'primevue/inputnumber';

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const adminSetupStore = useAdminSetupStore();
const { canEditConfigurableItem } = usePromoComposable();

const headers = ref(['', t('quote_config.days_zero'), t('quote_config.days_ninety_one'), t('quote_config.days_one_hundred_eighty')]);
const rows = ref<{ rowName: string; flightType: string; value: any[] }[]>([
  { rowName: t('quote_config.full_risk'), flightType: 'FULL_RISK', value: [0, 0, 0] },
  { rowName: t('quote_config.share_risk'), flightType: 'SHARED_RISK', value: [0, 0, 0] },
  { rowName: t('quote_config.fbr'), flightType: 'FBR', value: [0, 0, 0] },
  { rowName: t('quote_config.pre_reg'), flightType: 'PRE_REG', value: [0, 0, 0] }
]);

const getNetMarginConfig = () => {
  adminSetupStore.getNetMarginConfig().then((res: any) => {
    if (res && res.data) {
      rows.value.forEach((row: any) => {
        const dataFind = res.data.find((item: any) => item.flightType === row.flightType);
        row.value[0] = dataFind.days0;
        row.value[1] = dataFind.days91;
        row.value[2] = dataFind.days181;
        row.pk = dataFind.pk;
        row.sk = dataFind.sk;
      });
    }
  });
};

getNetMarginConfig();

const updateNetMarginConfig = (row: any) => {
  const payload = {
    ...row,
    days0: row.value[0],
    days91: row.value[1],
    days181: row.value[2]
  };
  delete payload.value;
  delete payload.rowName;
  adminSetupStore
    .updateNetMarginConfig(payload)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const updateModalValue = (index: number, indexNetMargin: number) => {
  if (!rows.value[index].value[indexNetMargin]) {
    rows.value[index].value[indexNetMargin] = 0;
  }
};

const onlyNumber = (event: any, index: number, indexNetMargin: number) => {
  if (!event.target.value) {
    rows.value[index].value[indexNetMargin] = null;
  }
};
</script>
<template>
  <div>
    <h4>{{ $t('quote_config.net_margin_safety').toLocaleUpperCase() }}</h4>
    <h5>{{ $t('quote_config.net_margin_threshold').toLocaleUpperCase() }}</h5>
    <div class="mt-5">
      <div class="flex mb-3">
        <div :class="{ 'w-17rem': !header, 'cell-width': header }" v-for="header in headers" :key="header">
          <b>{{ header }}</b>
        </div>
      </div>
      <div class="flex mb-3" v-for="(row, index) in rows" :key="index">
        <div class="align-self-center w-17rem">
          <b>{{ row.rowName }}</b>
        </div>
        <div class="cell-width" v-for="(item, indexNetMargin) in row.value" :key="indexNetMargin">
          <InputNumber v-model="row.value[indexNetMargin]" @update:modelValue="updateModalValue(index, indexNetMargin)" @keydown="onlyNumber($event, index, indexNetMargin)"></InputNumber>
        </div>
        <Button v-if="canEditConfigurableItem" :label="$t('promos.save')" icon="pi pi-check save-btn" class="mr-2 bg-primary" @click="updateNetMarginConfig(row)" />
      </div>
    </div>
  </div>
</template>
